import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { Platform, View } from "@-/com.npmjs.react-native";
import { useSharedValue } from "@-/com.npmjs.react-native-reanimated";
import { SuspensePageBoundary } from "@-/dev.improve.haniverse.components.ui.suspense-page";
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from "@-/dev.improve.haniverse.components.ui.popover";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { VisualActivityProvider } from "@-/dev.improve.haniverse.lib.visual-activity";
import { publishWelcomeGalaxyDiveTarget } from "@-/dev.improve.haniverse.routes.nav.app.auth.welcome-galaxy-entry";
import { useLearnMapModeSelector } from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.map-mode";
import {
  type ComponentRef,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LoadingMap, MapContent, type MapPresence } from "#map-content";
import { WelcomeHaniProvider } from "@-/dev.improve.haniverse.routes.nav.app.learn.summary/welcome-hani-provider";
import { LearnSummaryMapWelcomePresentationContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.summary";

/**
 * Learn's map, beneath the cards. Its silhouette is always drawn here (for the map the learner
 * last opened), so the moment the cards start to leave for the map the outline is already on
 * screen. The map's content is mounted ahead of time, once Learn is idle, so its sheet and buttons
 * slide in with the cards; it downloads its map only once opened, and draws it over that same
 * silhouette (see `ChinaMapLoadingCanvas`). It stops drawing while the cards cover it.
 *
 * Learn renders it at the bottom of its scene, under its toolbar, so the map runs beneath the
 * status bar and the map's own floating toolbar as it did on a page of its own. It takes touches
 * only while the map is Learn's screen.
 *
 * `welcome` heads the overview sheet on every opening: the greeting and
 * the way into the cards. The home is also where signing in lands: the welcome galaxy dives into
 * its core and dissolves into the map once it reports itself on screen here.
 */
export function MapLayer({ welcome = null }: { welcome?: ReactNode }) {
  const standaloneProgress = useSharedValue(1);
  const isLearnFocused = useIsFocused();
  // One presentation per account and local day, including when an unclaimed reward is dismissed.
  // Keep it above loading/content swaps so finishing the map download cannot reopen the popover.
  const [accountEmail] = useDeviceSetting("accountEmail");
  const [seenDays, setSeenDays] = useDeviceSetting("dailyRewardPopoverSeenDays");
  const [rewardDay, setRewardDay] = useState<string | null>(null);
  const offerDailyReward = useCallback(
    (day: string) => {
      const account = accountEmail ?? "";
      if (Platform.OS === "web" && seenDays[account] !== day) {
        setRewardDay(day);
        setSeenDays({ ...seenDays, [account]: day });
      }
    },
    [accountEmail, seenDays, setSeenDays],
  );
  const isRewardPopover = Platform.OS === "web" && rewardDay !== null;
  const welcomePresentation = useMemo(
    () => ({ isRewardPopover, offerDailyReward }),
    [isRewardPopover, offerDailyReward],
  );
  const sheetWelcome = isRewardPopover ? null : welcome;
  // Rendered on its own (a test), the map is simply open.
  const isPrepared = useLearnMapModeSelector((mode) => mode.isMapPrepared) ?? true;
  const isPresent = useLearnMapModeSelector((mode) => mode.isMapPresent) ?? true;
  const isLoadRequested = useLearnMapModeSelector((mode) => mode.isMapRequested) ?? true;
  const isShown = useLearnMapModeSelector((mode) => mode.isMapShown) ?? true;
  const isToolbarCurrent = useLearnMapModeSelector((mode) => mode.isMapToolbarCurrent) ?? true;
  const isHome = useLearnMapModeSelector((mode) => mode.isMapHome) ?? false;
  const progress = useLearnMapModeSelector((mode) => mode.progress) ?? standaloneProgress;
  const rewardTrigger = useRef<ComponentRef<typeof PopoverTrigger>>(null);
  useEffect(() => {
    if (isRewardPopover && welcome !== null && isShown && isLearnFocused) {
      rewardTrigger.current?.open();
    } else {
      rewardTrigger.current?.close();
    }
  }, [isLearnFocused, isRewardPopover, isShown, welcome]);
  // The silhouette is drawn from the first frame, so the home is on screen as soon as it is shown.
  useEffect(() => {
    if (!isHome || !isShown) {
      return;
    }
    publishWelcomeGalaxyDiveTarget(true);
    return () => publishWelcomeGalaxyDiveTarget(false);
  }, [isHome, isShown]);
  const presence = useMemo<MapPresence>(
    () => ({ isHome, isLoadRequested, isShown, isToolbarCurrent, progress }),
    [isHome, isLoadRequested, isShown, isToolbarCurrent, progress],
  );
  return (
    // Never flattened: the map's own floating toolbar carries a zIndex, which must stay inside this
    // layer rather than lift it above Learn's toolbar and cards as they slide over it.
    <View
      accessibilityElementsHidden={!isShown}
      className="absolute inset-0"
      collapsable={false}
      importantForAccessibility={isShown ? "auto" : "no-hide-descendants"}
      pointerEvents={isShown ? "box-none" : "none"}
      testID="learn-map-layer"
    >
      <LearnSummaryMapWelcomePresentationContext value={welcomePresentation}>
        <WelcomeHaniProvider active={!isRewardPopover && isShown && isLearnFocused}>
          {isPrepared ? (
            // Draws while it is on screen or sliding; a page opened over the map keeps it drawn, as
            // the Map tab's own stack did.
            <VisualActivityProvider active={isLearnFocused && isPresent}>
              <SuspensePageBoundary
                fallback={<MapLoadingShell presence={presence} welcome={sheetWelcome} />}
                location={import.meta.location}
              >
                <MapContent presence={presence} welcome={sheetWelcome} />
              </SuspensePageBoundary>
            </VisualActivityProvider>
          ) : (
            <MapLoadingShell presence={presence} welcome={sheetWelcome} />
          )}
          {Platform.OS === "web" ? (
            <Popover
              className="absolute inset-0"
              onOpenChange={(open) => {
                if (!open) {
                  setRewardDay(null);
                }
              }}
              pointerEvents="box-none"
            >
              <PopoverTrigger
                accessibilityLabel="Daily reward"
                className="pointer-events-none absolute bottom-6 left-1/2 size-0"
                ref={rewardTrigger}
                tabIndex={-1}
              />
              <PopoverContent
                aria-label="Daily reward"
                className="max-h-[90dvh] w-128 max-w-[calc(100vw-2rem)] overflow-y-auto"
                onCloseAutoFocus={(event) => event.preventDefault()}
                onOpenAutoFocus={(event) => event.preventDefault()}
                side="top"
                testID="learn-map-daily-reward-popover"
              >
                {welcome}
                <PopoverClose
                  accessibilityLabel="Dismiss daily reward"
                  className="absolute top-3 right-3 rounded-xs p-1 hover:bg-accent"
                >
                  <Icon className="size-4 text-foreground" name="LuX" />
                </PopoverClose>
              </PopoverContent>
            </Popover>
          ) : null}
        </WelcomeHaniProvider>
      </LearnSummaryMapWelcomePresentationContext>
    </View>
  );
}

/**
 * The first frame and router fallback: bundled map outline, region toolbar and an already-open
 * sheet. This shell never starts or awaits a map, travel, wallet or study-data query.
 */
export function MapLoadingShell({
  presence,
  welcome = null,
}: {
  presence?: MapPresence;
  welcome?: ReactNode;
}) {
  const progress = useSharedValue(1);
  const [regionId, setRegionId] = useDeviceSetting("mapRegion");
  return (
    <LoadingMap
      presence={
        presence ?? {
          isHome: true,
          isLoadRequested: false,
          isShown: true,
          isToolbarCurrent: true,
          progress,
        }
      }
      regionId={regionId}
      setRegionId={setRegionId}
      welcome={welcome}
    />
  );
}
